import type { DailyItem, DisciplioSettings, GoalMetric, HabitRepeat, ItemType, LegalAcceptance, MonthlyGoal, RagSource, UserProfile, WeeklyGoal } from "../disciplio/types";
import { defaultMetricUnitsForLanguage, mergeMetricUnits, normalizeMetricUnits, settingsWithMetricUnits } from "../disciplio/metric-units";
import { normalizeWeeklyGoalCategories, settingsWithWeeklyGoalCategories } from "../disciplio/weekly-goal-categories";
import type {
  ApiUser,
  DailyPlanItemResponse,
  GoalResponse,
  KnowledgeSourceResponse,
  MonthlyPlanItemResponse,
  MonthlyPlanResponse,
  WeeklyPlanItemResponse,
  WeeklyPlanResponse,
} from "./types";
import {
  clampHour,
  currentWeekStartKey,
  getStoredLanguage,
  isDateRange,
  normalizeLanguageForApi,
  normalizeTimeForApi,
  normalizeTimeForInput,
  parseApiList,
  parseJsonArray,
} from "./shared";

export function toDailyItemRequest(item: DailyItem) {
  return {
    title: item.title,
    itemType: item.type.toUpperCase(),
    timeRange: item.time,
    weeklyPlanItemId: item.weeklyGoalId && looksLikeUuid(item.weeklyGoalId) ? item.weeklyGoalId : null,
    contributionAmount: item.contributionAmount,
    contributionUnit: item.contributionUnit,
    repeatRule: JSON.stringify(item.repeat),
    timeConstraintType: item.timeConstraintType ?? null,
  };
}

export function toDailyItemUpdateRequest(item: DailyItem) {
  return {
    ...toDailyItemRequest(item),
    done: item.done,
    ...(item.weeklyGoalId === "" ? { clearWeeklyPlanItemLink: true } : {}),
  };
}

export function toMonthlyPlanItemRequest(goal: MonthlyGoal) {
  const isUnmeasured = !Number.isFinite(goal.target) || goal.target <= 0;
  return {
    monthlyPlanItemId: looksLikeUuid(goal.id) ? goal.id : null,
    yearGoalId: goal.yearGoalId && looksLikeUuid(goal.yearGoalId) ? goal.yearGoalId : null,
    title: goal.title,
    targetValue: isUnmeasured ? 0 : goal.target,
    targetUnit: isUnmeasured ? "" : goal.unit,
    progressValue: isUnmeasured ? 0 : goal.progress,
    completed: goal.completed,
  };
}

export function toWeeklyPlanItemRequest(goal: WeeklyGoal) {
  return {
    weeklyPlanItemId: looksLikeUuid(goal.id) ? goal.id : null,
    monthlyPlanItemId: goal.monthlyGoalId && looksLikeUuid(goal.monthlyGoalId) ? goal.monthlyGoalId : null,
    title: goal.title,
    targetValue: goal.target,
    targetUnit: goal.unit,
    progressValue: goal.progress,
    category: goal.category ?? "",
    completed: goal.completed,
  };
}

export function toKnowledgeSourceRequest(source: RagSource) {
  return {
    topic: source.topic,
    tags: source.principle,
    sourceType: source.sourceType.toUpperCase(),
    url: source.sourceUrl,
    confidence: source.confidence.toUpperCase(),
    language: normalizeLanguageForApi(getStoredLanguage()),
    content: source.content,
    actionableAdvice: source.actionableAdvice,
    active: true,
  };
}

export function profileFromApiUser(user: ApiUser, fallback?: UserProfile): UserProfile {
  return {
    userId: user.userId ?? fallback?.userId ?? "",
    onboardingCompleted: user.onboardingCompleted,
    authMode: "login",
    accountRole: user.role,
    fullName: user.fullName ?? fallback?.fullName ?? "",
    email: user.email ?? fallback?.email ?? "",
    password: "",
    role: user.roleContext ?? fallback?.role ?? "",
    interests: parseApiList(user.interests) ?? fallback?.interests ?? [],
    challengeAreas: parseApiList(user.challengeAreas) ?? fallback?.challengeAreas ?? [],
    hobbies: parseApiList(user.hobbies) ?? fallback?.hobbies ?? [],
    globalGoals: parseApiList(user.globalGoals) ?? fallback?.globalGoals ?? [],
    yearGoals: fallback?.yearGoals ?? [],
    globalGoalIds: fallback?.globalGoalIds ?? [],
    yearGoalIds: fallback?.yearGoalIds ?? [],
    yearGoalLinks: fallback?.yearGoalLinks ?? {},
    globalGoalCompleted: fallback?.globalGoalCompleted ?? {},
    yearGoalCompleted: fallback?.yearGoalCompleted ?? {},
    globalGoalMetrics: fallback?.globalGoalMetrics ?? {},
    yearGoalMetrics: fallback?.yearGoalMetrics ?? {},
    systemNeeds: parseApiList(user.systemNeeds) ?? fallback?.systemNeeds ?? [],
    weeklyCommitment: user.weeklyCommitment ?? fallback?.weeklyCommitment ?? "",
    motivation: user.motivation ?? fallback?.motivation ?? "",
    hasPassword: user.hasPassword ?? fallback?.hasPassword ?? false,
    authProviders: user.authProviders ?? fallback?.authProviders ?? [],
    legal: user.legal ? {
      currentTermsVersion: user.legal.currentTermsVersion,
      currentPrivacyVersion: user.legal.currentPrivacyVersion,
      termsAccepted: user.legal.termsAccepted,
      privacyAccepted: user.legal.privacyAccepted,
      termsAcceptedAt: user.legal.termsAcceptedAt ?? undefined,
      privacyAcceptedAt: user.legal.privacyAcceptedAt ?? undefined,
      aiConsentStatus: user.legal.aiConsentStatus,
      aiDisclosureVersion: user.legal.aiDisclosureVersion ?? undefined,
      aiDisclosureRespondedAt: user.legal.aiDisclosureRespondedAt ?? undefined,
    } satisfies LegalAcceptance : fallback?.legal,
  };
}

export function profileWithGoalsIndex(profile: UserProfile, goals: GoalResponse[]): UserProfile {
  const activeGoals = goals.filter((goal) => goal.status !== "DELETED");
  if (!activeGoals.length) return profile;

  const globals = activeGoals
    .filter((goal) => goal.scope === "GLOBAL")
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
  const years = activeGoals
    .filter((goal) => goal.scope === "YEAR")
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));

  const globalTitleById = new Map(globals.map((goal) => [goal.goalId, goal.title]));
  const globalGoals = globals.map((goal) => goal.title);
  const yearGoals = years.map((goal) => goal.title);
  const globalGoalIds = globals.map((goal) => goal.goalId);
  const yearGoalIds = years.map((goal) => goal.goalId);
  const yearGoalLinks: Record<string, string> = {};
  const yearGoalCompleted: Record<string, boolean> = { ...profile.yearGoalCompleted };
  const globalGoalCompleted: Record<string, boolean> = { ...profile.globalGoalCompleted };
  const yearGoalMetrics: Record<string, GoalMetric> = { ...profile.yearGoalMetrics };
  const globalGoalMetrics: Record<string, GoalMetric> = { ...profile.globalGoalMetrics };

  for (const global of globals) {
    globalGoalCompleted[global.title] = global.status === "ARCHIVED";
    if (global.targetValue > 0 && global.targetUnit.trim()) {
      globalGoalMetrics[global.title] = {
        target: Number(global.targetValue),
        unit: global.targetUnit,
        progress: Number(global.progressValue),
      };
    } else {
      delete globalGoalMetrics[global.title];
    }
  }

  for (const year of years) {
    yearGoalCompleted[year.title] = year.status === "ARCHIVED";
    if (year.targetValue > 0 && year.targetUnit.trim()) {
      yearGoalMetrics[year.title] = {
        target: Number(year.targetValue),
        unit: year.targetUnit,
        progress: Number(year.progressValue),
      };
    } else {
      delete yearGoalMetrics[year.title];
    }
    const parentTitle = year.parentGoalId ? globalTitleById.get(year.parentGoalId) : undefined;
    if (parentTitle) {
      yearGoalLinks[year.title] = parentTitle;
    }
  }

  return {
    ...profile,
    globalGoals,
    yearGoals,
    globalGoalIds,
    yearGoalIds,
    yearGoalLinks,
    yearGoalCompleted,
    globalGoalCompleted,
    yearGoalMetrics,
    globalGoalMetrics,
  };
}

export function settingsFromApiUser(user: ApiUser, fallback: DisciplioSettings): DisciplioSettings {
  const settings = user.settings;
  if (!settings) return fallback;
  const language = normalizeLanguageForApi(settings.language ?? fallback.language);
  const defaultMetricUnits = normalizeMetricUnits(settings.defaultMetricUnits, 30);
  const customMetricUnits = normalizeMetricUnits(settings.customMetricUnits);
  return settingsWithWeeklyGoalCategories(settingsWithMetricUnits({
    ...fallback,
    timezone: normalizeTimezone(settings.timezone, fallback.timezone),
    language,
    reflectionUnlockHour: clampHour(settings.reflectionUnlockHour, fallback.reflectionUnlockHour),
    dayStartTime: normalizeTimeForInput(settings.dayStartTime, fallback.dayStartTime),
    autoSuggestFocus: settings.autoSuggestFocus ?? fallback.autoSuggestFocus,
    showCompletedByDefault: settings.showCompletedByDefault ?? fallback.showCompletedByDefault,
    dailyGoalNotificationsEnabled: settings.dailyGoalNotificationsEnabled ?? fallback.dailyGoalNotificationsEnabled,
    dailyGoalEndReminderMinutes: clampReminderMinutes(settings.dailyGoalEndReminderMinutes, fallback.dailyGoalEndReminderMinutes),
    notificationSoundEnabled: settings.notificationSoundEnabled ?? fallback.notificationSoundEnabled,
    defaultMetricUnits: defaultMetricUnits.length ? defaultMetricUnits : defaultMetricUnitsForLanguage(language),
    customMetricUnits,
    metricUnits: normalizeMetricUnits(settings.metricUnits, 30).length
      ? normalizeMetricUnits(settings.metricUnits, 30)
      : mergeMetricUnits(defaultMetricUnits.length ? defaultMetricUnits : defaultMetricUnitsForLanguage(language), customMetricUnits),
    streakFreeWeekdays: parseJsonArray(settings.streakFreeWeekdays)
      .map((day) => Number(day))
      .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6),
    streakFreeDateRanges: parseJsonArray(settings.streakFreeDateRanges).filter(isDateRange),
    aiConsentStatus: settings.aiConsentStatus ?? fallback.aiConsentStatus ?? "UNDECIDED",
    aiDisclosureVersion: settings.aiDisclosureVersion ?? fallback.aiDisclosureVersion,
    aiDisclosureRespondedAt: settings.aiDisclosureRespondedAt ?? fallback.aiDisclosureRespondedAt,
    calendarHabitTimeScope:
      settings.calendarHabitTimeScope === "ONE" || settings.calendarHabitTimeScope === "FUTURE"
        ? settings.calendarHabitTimeScope
        : "calendarHabitTimeScope" in settings
          ? undefined
          : fallback.calendarHabitTimeScope,
    calendarHabitTimeScopeExpiresAt:
      "calendarHabitTimeScopeExpiresAt" in settings
        ? settings.calendarHabitTimeScopeExpiresAt ?? undefined
        : fallback.calendarHabitTimeScopeExpiresAt,
    dailyReflectionWorkflowEnabled: settings.dailyReflectionWorkflowEnabled ?? fallback.dailyReflectionWorkflowEnabled ?? false,
    defaultWeeklyGoalCategories: normalizeWeeklyGoalCategories(
      "defaultWeeklyGoalCategories" in settings ? settings.defaultWeeklyGoalCategories : fallback.defaultWeeklyGoalCategories,
      8,
    ),
    customWeeklyGoalCategories: normalizeWeeklyGoalCategories(
      "customWeeklyGoalCategories" in settings ? settings.customWeeklyGoalCategories : fallback.customWeeklyGoalCategories,
    ),
    weeklyGoalCategories: normalizeWeeklyGoalCategories(
      "weeklyGoalCategories" in settings ? settings.weeklyGoalCategories : fallback.weeklyGoalCategories,
      38,
    ),
    customWeeklyGoalCategoryLimit: "customWeeklyGoalCategoryLimit" in settings
      ? settings.customWeeklyGoalCategoryLimit ?? 10
      : fallback.customWeeklyGoalCategoryLimit ?? 10,
  }));
}

function clampReminderMinutes(value: number | undefined, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(0, Math.min(120, Math.round(value ?? fallback)));
}

function normalizeTimezone(value: string | undefined, fallback: string): string {
  if (!value || !value.trim()) return fallback;
  const timezone = value.trim();
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format();
    return timezone;
  } catch {
    return fallback;
  }
}

export function toRagSource(source: KnowledgeSourceResponse): RagSource {
  const now = new Date().toISOString();
  return {
    id: source.sourceId,
    topic: source.topic,
    principle: source.tags ?? "",
    content: source.content,
    actionableAdvice: source.actionableAdvice ?? "",
    sourceType: source.sourceType.toLowerCase() as RagSource["sourceType"],
    confidence: source.confidence.toLowerCase() as RagSource["confidence"],
    sourceUrl: source.url ?? "",
    ingestionStatus: normalizeIngestionStatus(source.ingestionStatus),
    errorMessage: source.errorMessage ?? undefined,
    createdAt: source.createdAt ?? now,
    updatedAt: source.updatedAt ?? now,
  };
}

export function toDailyItem(item: DailyPlanItemResponse): DailyItem {
  return {
    id: item.dailyPlanItemId,
    title: item.title,
    type: item.itemType.toLowerCase() as ItemType,
    time: item.timeRange ?? "",
    done: item.done,
    weeklyGoalId: item.weeklyPlanItemId ?? "",
    contributionAmount: Number(item.contributionAmount ?? 0),
    contributionUnit: item.contributionUnit ?? "",
    repeat: parseRepeat(item.repeatRule),
    contributed: item.contributed,
    recurring: item.recurring ?? false,
    occurrenceDate: item.occurrenceDate,
    occurrenceLocked: item.occurrenceLocked ?? false,
    timeConstraintType: item.timeConstraintType,
  };
}

export function toMonthlyGoal(item: MonthlyPlanItemResponse): MonthlyGoal {
  return {
    id: item.monthlyPlanItemId,
    title: item.title,
    target: Number(item.targetValue ?? 0),
    unit: item.targetUnit ?? "",
    progress: Number(item.progressValue ?? 0),
    yearGoalId: item.yearGoalId ?? undefined,
    completed: item.status === "ARCHIVED",
  };
}

export function toWeeklyGoal(item: WeeklyPlanItemResponse, weekStartDate?: string): WeeklyGoal {
  return {
    id: item.weeklyPlanItemId,
    title: item.title,
    target: Number(item.targetValue ?? 0),
    unit: item.targetUnit ?? "",
    progress: Number(item.progressValue ?? 0),
    weekStartDate,
    monthlyGoalId: item.monthlyPlanItemId ?? "",
    category: item.category ?? undefined,
    completed: item.status === "ARCHIVED",
  };
}

export function attachYearGoalTitles(monthlyGoals: MonthlyGoal[], goals: GoalResponse[]): MonthlyGoal[] {
  const yearGoalTitleById = new Map(
    goals.filter((goal) => goal.scope === "YEAR" && goal.status !== "DELETED").map((goal) => [goal.goalId, goal.title])
  );
  return monthlyGoals.map((goal) => ({
    ...goal,
    yearGoal: goal.yearGoal ?? (goal.yearGoalId ? yearGoalTitleById.get(goal.yearGoalId) ?? "" : ""),
  }));
}

export function currentMonthGoals(plans: MonthlyPlanResponse[], date = new Date()): MonthlyGoal[] {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return plans.find((plan) => plan.year === year && plan.month === month)?.items.map(toMonthlyGoal) ?? [];
}

export function currentWeekGoals(plans: WeeklyPlanResponse[], date = new Date()): WeeklyGoal[] {
  const weekStart = currentWeekStartKey(date);
  return plans.find((plan) => plan.weekStartDate === weekStart)?.items.map((item) => toWeeklyGoal(item, weekStart)) ?? [];
}

function normalizeIngestionStatus(value?: string): RagSource["ingestionStatus"] {
  const normalized = value?.toLowerCase();
  if (normalized === "chunked" || normalized === "embedded" || normalized === "error") return normalized;
  return "ready";
}

function parseRepeat(value?: string): HabitRepeat {
  if (!value) return { cadence: "none", interval: 1 };
  try {
    const parsed = JSON.parse(value) as Partial<HabitRepeat>;
    return {
      cadence: parsed.cadence ?? "none",
      interval: parsed.interval ?? 1,
      excludedWeekdays: parsed.excludedWeekdays,
      excludedDates: parsed.excludedDates,
    };
  } catch {
    return { cadence: "none", interval: 1 };
  }
}

function looksLikeUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export { normalizeLanguageForApi, normalizeTimeForApi };
